<script setup>
import { useId } from 'vue';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';

defineProps({
  reorderable: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  emoji: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggle']);
const contentId = useId();

const onToggle = () => {
  emit('toggle');
};
</script>

<template>
  <div class="text-sm">
    <div
      class="flex items-center w-full min-w-0 rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak"
      :class="{ 'rounded-bl-none rounded-br-none': isOpen }"
    >
      <span
        v-if="reorderable"
        class="contact-section-drag-handle flex items-center justify-center self-stretch w-7 ms-1 shrink-0 cursor-grab active:cursor-grabbing touch-none text-n-slate-9 hover:text-n-slate-11"
        :title="$t('HYTEC.REORDER_SECTION')"
        aria-hidden="true"
        @click.stop
      >
        <span class="i-lucide-grip-vertical size-3.5" />
      </span>
      <button
        type="button"
        class="flex flex-1 items-center min-w-0 min-h-9 m-0 pe-3 py-1.5 rounded-lg text-start touch-pan-y focus-visible:ring-2 focus-visible:ring-n-brand"
        :class="reorderable ? 'ps-1' : 'ps-3'"
        :aria-expanded="isOpen"
        :aria-controls="contentId"
        @click.stop="onToggle"
      >
        <span class="flex flex-1 items-center gap-2 min-w-0">
          <EmojiOrIcon
            v-if="icon || emoji"
            class="inline-block w-4 shrink-0"
            :icon="icon"
            :emoji="emoji"
          />
          <span class="text-n-slate-12 text-sm break-words min-w-0">
            {{ title }}
          </span>
        </span>
        <span
          class="flex justify-end w-4 ms-2 shrink-0 text-n-slate-11"
          aria-hidden="true"
        >
          <fluent-icon v-if="isOpen" size="16" icon="subtract" />
          <fluent-icon v-else size="16" icon="add" />
        </span>
      </button>
      <slot name="button" />
    </div>
    <div
      v-if="isOpen"
      :id="contentId"
      class="outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg"
      :class="compact ? 'p-0' : 'px-2 py-4'"
    >
      <slot />
    </div>
  </div>
</template>
